import React, { useEffect, useRef, useState } from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

export type FormNumericTextFieldProps = Omit<TextFieldProps, 'name' | 'value' | 'onChange'> & {
  name: string;
  allowDecimal?: boolean;
};

type NumericFieldInnerProps = TextFieldProps & {
  field: any;
  allowDecimal: boolean;
  error: boolean;
  helperText?: string;
};

const NumericFieldInner: React.FC<NumericFieldInnerProps> = ({
  field,
  label,
  allowDecimal,
  error,
  helperText,
  ...rest
}) => {
  const lastEmitted = useRef<number>(0);
  const [display, setDisplay] = useState<string>(field.value === 0 ? '0' : field.value ?? '');

  useEffect(() => {
    const v = field.value;
    const num = typeof v === 'number' && !isNaN(v) ? v : null;
    if (num !== null && num !== lastEmitted.current) {
      setDisplay(String(num));
    }
  }, [field.value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const regex = allowDecimal ? /^\d*\.?\d*$/ : /^\d*$/;
    if (!regex.test(val)) return;
    setDisplay(val);
    const num = val === '' || val === '.' ? 0 : parseFloat(val);
    lastEmitted.current = isNaN(num) ? 0 : num;
    field.onChange(isNaN(num) ? 0 : num);
  };

  return (
    <TextField
      {...rest}
      label={label}
      value={display}
      onChange={handleChange}
      error={error}
      helperText={helperText}
    />
  );
};

export const FormNumericTextField: React.FC<FormNumericTextFieldProps> = ({
  name,
  label,
  allowDecimal = false,
  ...rest
}) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <NumericFieldInner
          {...rest}
          field={field}
          label={label}
          allowDecimal={allowDecimal}
          error={!!error}
          helperText={error?.message}
        />
      )}
    />
  );
};
